import React, { useMemo, useCallback } from 'react';
import { Modal, Button, Text, Group, ButtonGroup } from '@landr/maestro';
import { Plural, Trans } from '@lingui/macro';
import { useSelectedSamplesContext } from 'contexts/SelectedSamplesContext/SelectedSamplesContext';
import { useCollectionModalContext } from 'modals/CollectionModal/CollectionModalContext';
import { useMutation } from '@apollo/client';
import {
    RemoveSamplesFromUserCollectionMutation,
    UserCollectionQuery,
} from 'apollo/userCollection/userCollection.gql';
import { QueryConfig } from 'apollo/userCollection/queryConfig';
import { evictCacheByKeys } from 'helpers';
import { useNotificationsContext } from 'contexts/NotificationsContext/NotificationsContext';
import {
    RemoveMultipleSamplesFromCollectionError,
    RemoveMultipleSamplesFromCollectionSuccess,
    RemoveSingleSampleFromCollectionError,
    RemoveSingleSampleFromCollectionSuccess,
} from 'contexts/NotificationsContext/notifications';
import { ModalProps } from '../Modal.types';

interface RemoveSamplesFromCollectionModalContentProps {
    handleSampleRemove: () => void;
    isLoading: boolean;
    samplesCount: number;
    collectionName: string;
    handleClose: () => void;
}

export const RemoveSamplesFromCollectionModalContent: React.FC<
    RemoveSamplesFromCollectionModalContentProps
> = ({
    handleClose,
    samplesCount,
    handleSampleRemove,
    isLoading,
    collectionName,
}) => (
    <Modal.Content>
        <Modal.Section>
            <Group direction="vertical" gutter="xl" mt="sm">
                <Group.Item>
                    <Text as="h1" size="xl" fontWeight="bold">
                        <Plural
                            value={samplesCount}
                            one="Remove sample?"
                            other="Remove samples?"
                        />
                    </Text>
                </Group.Item>
                <Group.Item>
                    <Text size="md" contrast={'low'}>
                        <Plural
                            value={samplesCount}
                            one={`This sample will be removed from ${collectionName}.`}
                            other={`# samples will be removed from ${collectionName}.`}
                        />
                    </Text>
                </Group.Item>
            </Group>
        </Modal.Section>
        <Modal.Footer>
            <ButtonGroup>
                <Button
                    variant="secondary"
                    kind="minimal"
                    onClick={handleClose}
                >
                    <Trans>Cancel</Trans>
                </Button>
                <Button
                    variant="danger"
                    onClick={handleSampleRemove}
                    isLoading={isLoading}
                >
                    <Trans>Remove</Trans>
                </Button>
            </ButtonGroup>
        </Modal.Footer>
    </Modal.Content>
);

export const RemoveSamplesFromCollection: React.FC<ModalProps> = ({
    handleClose,
    isOpen,
}) => {
    const { clearSamplesSelection } = useSelectedSamplesContext();

    const { collection, samplesToDelete } = useCollectionModalContext();

    const { createNotification } = useNotificationsContext();

    const sampleIds = useMemo(
        () => samplesToDelete.map((samples) => samples?.guid),
        [samplesToDelete],
    );

    const handleCompleted = useCallback(() => {
        handleClose(() => {
            setTimeout(() => {
                clearSamplesSelection();
                if (!collection?.name) {
                    return;
                }

                if (samplesToDelete.length > 1) {
                    createNotification(
                        new RemoveMultipleSamplesFromCollectionSuccess(
                            collection.name,
                            samplesToDelete.length,
                        ),
                    );
                } else {
                    createNotification(
                        new RemoveSingleSampleFromCollectionSuccess(
                            collection.name,
                            samplesToDelete[0].name,
                        ),
                    );
                }
            }, 300);
        });
    }, [
        handleClose,
        clearSamplesSelection,
        createNotification,
        collection?.name,
        samplesToDelete,
    ]);

    const handleError = useCallback(() => {
        handleClose(() => {
            setTimeout(() => {
                clearSamplesSelection();

                if (!collection?.name) {
                    return;
                }

                if (samplesToDelete.length > 1) {
                    createNotification(
                        new RemoveMultipleSamplesFromCollectionError(
                            collection.name,
                            samplesToDelete.length,
                        ),
                    );
                } else {
                    createNotification(
                        new RemoveSingleSampleFromCollectionError(
                            collection.name,
                            samplesToDelete[0].name,
                        ),
                    );
                }
            }, 300);
        });
    }, [
        handleClose,
        clearSamplesSelection,
        collection?.name,
        createNotification,
        samplesToDelete,
    ]);

    const [requestRemoveSamplesFromUserCollection, { loading }] = useMutation(
        RemoveSamplesFromUserCollectionMutation,
        {
            variables: { collectionGuid: collection?.guid, sampleIds },
            // refetch is needed to get new generated artwork
            refetchQueries: [
                {
                    query: UserCollectionQuery,
                    variables: { collectionGuid: collection?.guid },
                    fetchPolicy: 'network-only',
                },
            ],
            awaitRefetchQueries: true,
            onCompleted: handleCompleted,
            onError: handleError,
            update: (cache) => {
                evictCacheByKeys(cache, [
                    collection?.guid,
                    QueryConfig.pathToItems,
                    'userCollectionsFeed',
                ]);
            },
        },
    );

    if (!sampleIds?.length || !collection) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} onClose={handleClose}>
            <RemoveSamplesFromCollectionModalContent
                handleClose={handleClose}
                handleSampleRemove={requestRemoveSamplesFromUserCollection}
                isLoading={loading}
                samplesCount={sampleIds.length}
                collectionName={collection.name}
            />
        </Modal>
    );
};

export default RemoveSamplesFromCollection;
